<template>
  <div
    v-track-scroll-next="tipBoxScrollTrack"
    class="dn-tip-box"
  >
    <atlas-headline
      tag="h3"
      class="title"
      :text="title"
    />
    <div class="content">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-html="description" />

      <template v-if="isBylineVariant">
        <div
          v-for="author in authorItems"
          :key="author.key"
          class="tips-box-author"
        >
          <div class="author-container">
            <div class="info">
              <atlas-headline tag="h4">
                {{ author.name }}
              </atlas-headline>
              <atlas-link
                v-track-event-next="() => getTipBoxAuthorEmailTrack(author)"
                class="email"
                :title="author.email"
                :to="`mailto:${author.email}`"
              />
            </div>
          </div>
        </div>
      </template>

      <atlas-link
        v-else-if="buttonTo"
        v-track-event-next="tipBoxButtonTrack"
        class="atlas-button primary"
        :title="buttonTitle"
        :to="buttonTo"
        external
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { AtlasLink, AtlasHeadline } from '@nhst/ui';
import { type PropType } from 'vue';
import { type ArticleBylineAuthor, type TipBoxVariant } from '~/types/article';
import { type PublicationContext } from '~/types';

interface TipBoxAuthorItem {
  key: string;
  name: string;
  email: string;
}

const props = defineProps({
  variant: {
    type: String as PropType<TipBoxVariant>,
    default: ''
  },
  authors: {
    type: Array as PropType<ArticleBylineAuthor[]>,
    default: () => []
  }
});

const rtConfPub = useRuntimeConfig().public as unknown as PublicationContext;

const tipBox = rtConfPub.tipBox;

const buttonTo = tipBox?.buttonTo ?? null;
const title = tipBox?.title || 'Har du tips?';
const description = tipBox?.description || 'Send oss vanlige nyhetstips, pressemeldinger eller innspill til saker.';
const buttonTitle = tipBox?.button || 'Tips oss';

const normalizedVariant = computed(() => props.variant?.toLowerCase() ?? '');

const authorItems = computed<TipBoxAuthorItem[]>(() =>
  props.authors
    .filter(author => author.email)
    .map(author => ({
      key: author.sourceId,
      name: author.name,
      email: author.email
    }))
);

const isBylineVariant = computed(() => normalizedVariant.value === 'byline' && authorItems.value.length > 0);
const tipBoxButtonTrack = computed(() => ({
  component: {
    zone: 'dn-tip-box',
    element: 'tip-box-button',
    properties: { variant: normalizedVariant.value }
  },
  content: {
    id: null,
    title: buttonTitle,
    type: 'button'
  }
}));

const tipBoxScrollTrack = computed(() => ({
  component: {
    zone: 'article-body',
    element: 'dn-tip-box',
    properties: { variant: normalizedVariant.value }
  },
  trackOptions: { trackCount: 1, trackDirection: 'top' }
}));

const getTipBoxAuthorEmailTrack = (author: TipBoxAuthorItem) => ({
  component: {
    zone: 'dn-tip-box',
    element: 'tip-box-email',
    properties: { variant: normalizedVariant.value }
  },
  content: {
    id: null,
    title: author.email,
    type: 'link'
  },
  attributes: {
    identifier_type: 'tip-box-email',
    identifier_value: author.email
  }
});

</script>

<style lang="scss">
.dn-tip-box {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: var(--color-ui-factbox-bg);
  color: var(--color-ui-factbox-fg);
  padding: 16px;
  border-radius: var(--atlas-radius);

  .title {
    --atlas-headline_font-family: var(--typography-editorial-infobox-title-min-font-family);
    --atlas-headline_font-size: var(--typography-editorial-infobox-title-min-font-size);
    --atlas-headline_font-weight: var(--typography-editorial-infobox-title-min-font-weight);
    --atlas-headline_line-height: var(--typography-editorial-infobox-title-min-line-height);
    --atlas-headline_letter-spacing: var(--typography-editorial-infobox-title-min-letter-spacing);
    --atlas-headline_spacing: 0;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 16px;

    p {
      font-size: var(--typography-utility-m-font-size);
      font-weight: var(--typography-utility-m-font-weight);
      line-height: var(--typography-utility-m-line-height);
      font-family: var(--typography-utility-m-font-family);

      a {
        color: inherit;
        text-decoration: underline;
      }
    }

    // The description uses a double <br> so mobile gets a double line break.
    // On desktop, collapse it back to a single break by hiding the second <br>.
    @media only screen and (width >= 768px) {
      p br + br {
        display: none;
      }
    }

    .atlas-link.atlas-button {
      align-self: flex-start;
      text-decoration: none;
    }

    .tips-box-author {
      display: flex;
      flex-direction: row;
      gap: var(--atlas_gap);
      align-items: center;

      .author-container {
        display: flex;
        flex-direction: row;
        gap: var(--atlas_gap);
        align-items: center;

        .info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: var(--typography-utility-s-font-size);
          font-weight: var(--typography-utility-s-font-weight);
          line-height: var(--typography-utility-s-line-height);
          font-family: var(--typography-utility-s-font-family);
          color: var(--color-typography-default);

          --atlas-headline_font-size: var(--typography-utility-m-font-size);
          --atlas-link_color: var(--color-typography-secondary);
          --atlas-link_font-family: var(--typography-utility-s-font-family);
          --atlas-link_font-size: var(--typography-utility-s-font-size);
          --atlas-link_font-weight: var(--typography-utility-s-font-weight);
          --atlas-link_line-height: var(--typography-utility-s-line-height);
          --atlas-link_letter-spacing: var(--typography-utility-s-letter-spacing);
          --atlas-link_text-decoration-color: var(--color-typography-secondary);
        }
      }
    }
  }
}
</style>
